export enum Permissions {
  eWorkbag_Component_Tools_Manage_Id = 37,
  eWorkbag_JobExternal_Manage_Id = 90,
  eWorkbag_JobExternal_View_Id = 91,
}

export const ROLE = {
  ADMIN: "admin",
  QA: "qa",
  USER: "user",
} as const;

export type RoleType = (typeof ROLE)[keyof typeof ROLE];

/**
 * Parse roles string from user profile into array of lowercase role names
 * @param rolesString - Comma-separated roles (e.g., "Admin, QA")
 * @returns Array of lowercase role names
 */
export function parseRoles(rolesString?: string | null): string[] {
  if (!rolesString) return [];
  return rolesString
    .split(",")
    .map((r) => r.trim().toLowerCase())
    .filter((r) => r.length > 0);
}

/**
 * Check if user has a specific role
 * @param userRoles - Array of user's roles (lowercase)
 * @param requiredRole - Role to check (case-insensitive)
 * @returns true if user has the role
 */
export function hasRole(userRoles: string[], requiredRole: string): boolean {
  return userRoles.includes(requiredRole.toLowerCase());
}

/**
 * Check if user has any of the specified roles
 * @param userRoles - Array of user's roles (lowercase)
 * @param allowedRoles - Array of roles to check
 * @returns true if user has at least one of the roles
 */
export function hasAnyRole(
  userRoles: string[],
  allowedRoles: string[]
): boolean {
  if (allowedRoles.length === 0) return true;
  return allowedRoles.some((r) => userRoles.includes(r.toLowerCase()));
}

/**
 * Check if user does NOT have any of the specified roles (exclusion check)
 * @param userRoles - Array of user's roles (lowercase)
 * @param excludedRoles - Array of roles to exclude
 * @returns true if user does NOT have any of the excluded roles
 */
export function hasNoExcludedRole(
  userRoles: string[],
  excludedRoles: string[]
): boolean {
  if (excludedRoles.length === 0) return true;
  return !excludedRoles.some((r) => userRoles.includes(r.toLowerCase()));
}

/**
 * Parse permission string from user profile into array of numbers
 * @param permissionString - Comma-separated permission numbers (e.g., "1,2,3,4")
 * @returns Array of permission numbers
 */
export function parsePermissions(permissionString?: string | null): number[] {
  if (!permissionString) return [];
  return permissionString
    .split(",")
    .map(Number)
    .filter((n) => !isNaN(n));
}

/**
 * Check if user has a specific permission
 * @param userPermissions - Array of user's permission numbers
 * @param requiredPermission - Permission number to check
 * @returns true if user has the permission
 */
export function hasPermission(
  userPermissions: number[],
  requiredPermission?: number | string
): boolean {
  if (requiredPermission === undefined) return true;

  // Convert string to number if needed (for backward compatibility during migration)
  const permissionNumber =
    typeof requiredPermission === "string"
      ? Number(requiredPermission)
      : requiredPermission;

  // Check if conversion resulted in NaN
  if (isNaN(permissionNumber)) return false;

  return userPermissions.includes(permissionNumber);
}

/**
 * Check if user has a specific permission (string-based)
 * For rolePermissions like "Planner_Machine_ManageMachine"
 * @param userPermissions - Array of user's permission strings
 * @param requiredPermission - Permission string to check
 * @returns true if user has the permission
 */
export function hasRolePermission(
  userPermissions: string[],
  requiredPermission?: string
): boolean {
  if (!requiredPermission) return true;
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if user has any of the specified permissions
 * @param userPermissions - Array of user's permission numbers
 * @param requiredPermissions - Array of permission numbers to check
 * @returns true if user has at least one of the permissions
 */
export function hasAnyPermission(
  userPermissions: number[],
  requiredPermissions: number[]
): boolean {
  if (requiredPermissions.length === 0) return true;
  return requiredPermissions.some((p) => userPermissions.includes(p));
}

/**
 * Check if user has all of the specified permissions
 * @param userPermissions - Array of user's permission numbers
 * @param requiredPermissions - Array of permission numbers to check
 * @returns true if user has all of the permissions
 */
export function hasAllPermissions(
  userPermissions: number[],
  requiredPermissions: number[]
): boolean {
  if (requiredPermissions.length === 0) return true;
  return requiredPermissions.every((p) => userPermissions.includes(p));
}

// ===== NAVIGATION FILTERING TYPES =====

// Base route type for sidebar menu (uses number permission)
export type NavigationRoute = {
  title: string;
  url: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>> | React.ElementType;
  permissions?: string[];
  capabilities?: number[];
  /** Roles that are allowed to see this route (if specified, user must have at least one) */
  allowedRoles?: string[];
  /** Roles that are excluded from seeing this route (if specified, user must NOT have any) */
  excludedRoles?: string[];
  children?: NavigationRoute[];
};

// Sidebar-style navigation group (group -> routes)
export type SidebarNavigationGroup<
  T extends NavigationRoute = NavigationRoute,
> = {
  group: string;
  routes: T[];
};

// Floating nav-style navigation group (group -> items -> routes)
export type FloatingNavigationGroup<
  T extends NavigationRoute = NavigationRoute,
> = {
  group: string;
  items: {
    menu: string;
    routes: T[];
  }[];
};

// Union type for both navigation structures
export type NavigationGroup<T extends NavigationRoute = NavigationRoute> =
  | SidebarNavigationGroup<T>
  | FloatingNavigationGroup<T>
  | { group: string; routes?: T[]; items?: { menu: string; routes: T[] }[] };

/**
 * Check if a route is accessible based on permissions and roles
 * @param route - Navigation route to check
 * @param userStringPermissions - Array of user's string permissions (rolePermissions)
 * @param userRoles - Array of user's roles (lowercase)
 * @returns true if user can access the route
 */
function isRouteAccessible<T extends NavigationRoute>(
  route: T,
  userStringPermissions: string[],
  userRoles: string[]
): boolean {
  // Check permissions - user must have ANY permission in the array (matches stea-tool-ui's isRouteAccessible)
  if (route.permissions && route.permissions.length > 0) {
    const hasAnyPerm = route.permissions.some((perm) =>
      hasRolePermission(userStringPermissions, perm)
    );

    if (!hasAnyPerm) {
      return false;
    }
  }

  // Check allowed roles (if specified, user must have at least one)
  if (route.allowedRoles && route.allowedRoles.length > 0) {
    if (!hasAnyRole(userRoles, route.allowedRoles)) {
      return false;
    }
  }

  // Check excluded roles (if specified, user must NOT have any)
  if (route.excludedRoles && route.excludedRoles.length > 0) {
    if (!hasNoExcludedRole(userRoles, route.excludedRoles)) {
      return false;
    }
  }

  return true;
}

/**
 * Filter navigation routes based on user permissions and roles
 * Works with both sidebar menu structure and floating navigation structure
 * @param routes - Array of navigation routes
 * @param userPermissions - Array of user's permission numbers
 * @param userStringPermissions - Array of user's string permissions (rolePermissions)
 * @param userRoles - Array of user's roles (lowercase)
 * @returns Filtered array of routes user has access to
 */
export function filterRoutesByPermission<T extends NavigationRoute>(
  routes: T[],
  userPermissions: number[],
  userStringPermissions: string[] = [],
  userRoles: string[] = []
): T[] {
  return routes
    .filter((route) =>
      isRouteAccessible(route, userStringPermissions, userRoles)
    )
    .map((route) => {
      if (route.children && route.children.length > 0) {
        const filteredChildren = filterRoutesByPermission(
          route.children,
          userPermissions,
          userStringPermissions,
          userRoles
        );
        return { ...route, children: filteredChildren };
      }
      return route;
    })
    .filter((route) => {
      // Remove parent items that have no accessible children
      if (route.children !== undefined && route.children.length === 0) {
        return false;
      }
      return true;
    });
}

/**
 * Filter sidebar navigation groups based on user permissions and roles
 * @param groups - Array of sidebar navigation groups (group -> routes)
 * @param userPermissions - Array of user's permission numbers
 * @param userStringPermissions - Array of user's string permissions (rolePermissions)
 * @param userRoles - Array of user's roles (lowercase)
 * @returns Filtered array of groups with accessible routes
 */
export function filterSidebarNavigation<T extends NavigationRoute>(
  groups: SidebarNavigationGroup<T>[],
  userPermissions: number[],
  userStringPermissions: string[] = [],
  userRoles: string[] = []
): SidebarNavigationGroup<T>[] {
  return groups
    .map((group) => ({
      ...group,
      routes: filterRoutesByPermission(
        group.routes,
        userPermissions,
        userStringPermissions,
        userRoles
      ),
    }))
    .filter((group) => group.routes.length > 0);
}

/**
 * Filter floating navigation groups based on user permissions and roles
 * @param groups - Array of floating navigation groups (group -> items -> routes)
 * @param userPermissions - Array of user's permission numbers
 * @param userStringPermissions - Array of user's string permissions (rolePermissions)
 * @param userRoles - Array of user's roles (lowercase)
 * @returns Filtered array of groups with accessible routes
 */
export function filterFloatingNavigation<T extends NavigationRoute>(
  groups: FloatingNavigationGroup<T>[],
  userPermissions: number[],
  userStringPermissions: string[] = [],
  userRoles: string[] = []
): FloatingNavigationGroup<T>[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => ({
          ...item,
          routes: filterRoutesByPermission(
            item.routes,
            userPermissions,
            userStringPermissions,
            userRoles
          ),
        }))
        .filter((item) => item.routes.length > 0),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Generic filter for both navigation types
 * Detects structure and applies appropriate filtering
 */
export function filterNavigationGroups<T extends NavigationRoute>(
  groups: NavigationGroup<T>[],
  userPermissions: number[],
  userRoles: string[] = []
): NavigationGroup<T>[] {
  return groups
    .map((group) => {
      // Handle sidebar structure (group -> routes)
      if ("routes" in group && group.routes) {
        const filteredRoutes = filterRoutesByPermission(
          group.routes,
          userPermissions,
          userRoles
        );
        return { ...group, routes: filteredRoutes };
      }

      // Handle floating navigation structure (group -> items -> routes)
      if ("items" in group && group.items) {
        const filteredItems = group.items
          .map((item) => ({
            ...item,
            routes: filterRoutesByPermission(
              item.routes,
              userPermissions,
              userRoles
            ),
          }))
          .filter((item) => item.routes.length > 0);
        return { ...group, items: filteredItems };
      }

      return group;
    })
    .filter((group) => {
      if ("routes" in group && group.routes) return group.routes.length > 0;
      if ("items" in group && group.items) return group.items.length > 0;
      return false;
    });
}
